import React, { ForwardedRef, forwardRef, ReactElement, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthenticatedRoutes, useRouteResources } from '../routers/AuthenticatedRoutes';
import { useResourceChecker } from './resource';
import { ArgsType } from '../types/ArgsType';
import { useMemoWithEqualityCheck } from '../hooks/useMemoWithEqualityCheck';

type AuthenticatedRouteKey = keyof typeof AuthenticatedRoutes;

interface IProps<T extends AuthenticatedRouteKey> {
  children: React.ReactNode;
  showFallback?: boolean;
  route: T;
  params: Parameters<typeof AuthenticatedRoutes[T]>;
  query?: Record<string, string | number>;
  keepQuery?: boolean;
}

const isEqual = (oldParams: any[], newParams: any[]) => {
  return oldParams.length === newParams.length && oldParams.every((val, index) => val === newParams[index]);
};

export const useIsGuardedRouteAllowed = <RouteName extends AuthenticatedRouteKey>(
  route: RouteName,
  params: ArgsType<typeof AuthenticatedRoutes[RouteName]>
) => {
  const resourceChecker = useResourceChecker();
  const routeResources = useRouteResources();
  const resourceGetter = routeResources[route];
  const paramsMemo = useMemoWithEqualityCheck(params, isEqual);

  const resource = useMemo(
    () =>
      resourceGetter(
        /**
         * this is legit, but typescript can't see that
         */
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...paramsMemo
      ),
    [paramsMemo, resourceGetter]
  );

  if (typeof resource === 'boolean') {
    return resource;
  } else if (Array.isArray(resource)) {
    return resource.every((resource) => resourceChecker(resource));
  } else {
    return resourceChecker(resource);
  }
};

export const GuardedRouteCondition = <RouteName extends AuthenticatedRouteKey>({
  route,
  params,
  children,
  fallback,
}: {
  route: RouteName;
  params: ArgsType<typeof AuthenticatedRoutes[RouteName]>;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) => {
  const isAllowed = useIsGuardedRouteAllowed(route, params);

  if (isAllowed) return <>{children}</>;
  return fallback ? <>{fallback}</> : null;
};

const GuardedRouteLinkComponent: <Route extends AuthenticatedRouteKey>(
  props: IProps<Route>,
  ref: ForwardedRef<HTMLAnchorElement>
) => ReactElement | null = ({ route, params, query = {}, keepQuery = false, showFallback = false, ...rest }, ref) => {
  const { search } = useLocation();

  const url = (AuthenticatedRoutes[route] as (...p: typeof params) => string)(...params);

  const linkSearch = useMemo(() => {
    const searchValue = keepQuery ? new URLSearchParams(search) : new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      searchValue.set(key, typeof value === 'number' ? value.toString() : value);
    }
    return searchValue.toString();
  }, [JSON.stringify(query), keepQuery, search]);

  return (
    <GuardedRouteCondition route={route} params={params} fallback={showFallback ? rest.children : void 0}>
      <Link ref={ref} to={`${url}?${linkSearch}`} {...rest} />
    </GuardedRouteCondition>
  );
};
export const GuardedRouteLink = forwardRef(GuardedRouteLinkComponent);
